import React, { useState, useRef } from "react";
import swal from "sweetalert";
import {
  GetCustomerDetails,
  GetProductDetails,
  GetOrderDetails,
} from "../../../../services";
import { Button, TextField, InputAdornment } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Search } from "@material-ui/icons";

export default function AdminOrderCreate() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [grandTotal, setGrandTotal] = useState("");
  const [page, setPage] = useState(1);
  const [customerId, setCustomerId] = useState("");
  const [variants, setVariants] = useState([]);
  const [productId, setProductId] = useState("");
  const [variantIds, setVariantIds] = useState([]);
  const [orderProducts, setOrderProducts] = useState([]);
  const rowsPerPage = 5;

  const productSectionRef = useRef(null);

  const addProductToOrder = () => {
    if (!selectedProduct || variantIds.length === 0) {
      swal("Error", "Select product and variants", "error");
      return;
    }

    // ❌ Prevent duplicate
    const alreadyExists = orderProducts.find(
      (item) => item.productId === selectedProduct.id,
    );

    if (alreadyExists) {
      swal("Error", "Product already added", "warning");
      return;
    }

    const newItem = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      image: selectedProduct.photo,
      variants: variantIds,
    };

    setOrderProducts([...orderProducts, newItem]);

    setSelectedProduct(null);
    setVariants([]);
    setVariantIds([]);
    setProductQuery("");
    setFilteredProducts([]);
  };

  const searchCustomers = async () => {
    try {
      setLoadingCustomers(true);

      const response = await GetCustomerDetails.searchAllCustomers({
        query: searchQuery,
      });

      setFilteredCustomers(response.customers || []);
      setPage(1);
    } catch (error) {
      swal("Error", "Failed to search customers", "error");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const searchProducts = async () => {
    try {
      const res =
        await GetProductDetails.getAllProductQuerySearchList(productQuery);

      if (res && res.products) {
        const uniqueProducts = res.products.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.id === value.id),
        );

        setFilteredProducts(uniqueProducts);
      } else {
        setFilteredProducts([]);
      }
    } catch (error) {
      swal("Error", "Product search failed", "error");
    }
  };

  const selectCustomer = (customer) => {
    setCustomerId(customer.id);
    setSelectedCustomer(customer);

    setTimeout(() => {
      if (productSectionRef.current) {
        const y =
          productSectionRef.current.getBoundingClientRect().top +
          window.pageYOffset -
          80; // offset from top

        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 200);
  };

  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setProductId(product.id);

    const list = await GetProductDetails.getProductById(product.id);

    if (list && list.data && list.data.length > 0) {
      const formattedData = list.data[0];

      setVariants(formattedData.productvariants || []);
    }
  };

  const removeProductFromOrder = (index) => {
    const updated = orderProducts.filter((_, i) => i !== index);
    setOrderProducts(updated);
  };

  const resetEntireForm = () => {
    // Customer section
    setCustomers([]);
    setFilteredCustomers([]);
    setSearchQuery("");
    setSelectedCustomer(null);
    setCustomerId("");
    setPage(1);

    // Product section
    setProductQuery("");
    setFilteredProducts([]);
    setSelectedProduct(null);
    setVariants([]);
    setProductId("");
    setVariantIds([]);

    // Order section
    setOrderProducts([]);
    setOrderId("");
    setOrderDate("");
    setGrandTotal("");

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitOrder = async () => {
    // 1️⃣ Customer Validation
    if (!customerId) {
      swal("Customer Required", "Please select a customer", "error");
      return;
    }

    // 2️⃣ Products Validation
    if (orderProducts.length === 0) {
      swal("Products Required", "Please add at least one product", "error");
      return;
    }

    // 3️⃣ Order Date Validation
    if (!orderDate) {
      swal("Order Date Required", "Please select order date", "error");
      return;
    }

    // 4️⃣ Grand Total Validation
    if (!grandTotal || Number(grandTotal) <= 0) {
      swal("Grand Total Required", "Please enter valid grand total", "error");
      return;
    }

    // 🔥 FORMAT PRODUCTS
    const formattedProducts = orderProducts.map((item) => ({
      productId: item.productId,
      variantIds: item.variants.map((v) => v.id),
    }));

    const payload = {
      orderId,
      orderDate,
      customerId,
      products: formattedProducts,
      grandTotal: Number(grandTotal).toFixed(2),
    };

    const response = await GetOrderDetails.createAdminOrder(payload);

    if (response) {
      swal("Success", "Order Created Successfully", "success").then(() => {
        resetEntireForm();
      });
    }
  };

  const startIndex = (page - 1) * rowsPerPage;
  const paginatedCustomers = filteredCustomers.slice(
    startIndex,
    startIndex + rowsPerPage,
  );

  const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);

  return (
    <div className="container">
      <h2>Create Admin Order</h2>

      {/* Customer */}
      {/* ================= CUSTOMER SECTION ================= */}

      <div className="card card-static-2 mt-30 mb-30">
        <div className="card-title-2">
          <h4>Select Customer</h4>
        </div>

        <div className="card-body-table">
          {/* Search Area */}
          <div style={{ maxWidth: "420px", marginBottom: "20px" }}>
            <TextField
              variant="outlined"
              placeholder="Search by Name / Phone / Email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              margin="normal"
              disabled={!!selectedCustomer}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
                style: { height: 46 },
              }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{
                height: 46,
                marginTop: 12,
                transition: "all 0.3s ease",
              }}
              disabled={!!selectedCustomer}
              onClick={searchCustomers}
            >
              Search
            </Button>
          </div>

          {/* Selected Customer Display */}
          {selectedCustomer && (
            <div
              style={{
                background: "#f0f4ff",
                padding: 20,
                borderRadius: 8,
                marginBottom: 25,
                border: "1px solid #d6e0ff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircleIcon style={{ color: "#4caf50", fontSize: 28 }} />

                <div>
                  <strong style={{ fontSize: 16 }}>
                    {selectedCustomer.id} - {selectedCustomer.fullName}
                  </strong>
                  <div style={{ marginTop: 4 }}>
                    {selectedCustomer.phone} | {selectedCustomer.email}
                  </div>
                </div>
              </div>

              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setCustomerId("");
                  setSelectedCustomer(null);
                  setSearchQuery("");
                }}
              >
                Change Customer
              </Button>
            </div>
          )}

          {/* Customer Table */}
          {loadingCustomers && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div className="spinner-border text-primary" role="status" />
              <div style={{ marginTop: 10 }}>Loading customers...</div>
            </div>
          )}

          {!loadingCustomers && filteredCustomers.length > 0 && (
            <div
              className="table-responsive"
              style={{
                padding: 15,
                border: "1px solid #eee",
                borderRadius: 8,
                background: "#fff",
                transition: "all 0.4s ease",
                transform: selectedCustomer ? "scale(0.98)" : "scale(1)",
                opacity: selectedCustomer ? 0.5 : 1,
                filter: selectedCustomer ? "blur(2px)" : "blur(0px)",
                pointerEvents: selectedCustomer ? "none" : "auto",
                animation: "fadeInTable 0.4s ease",
              }}
            >
              <table className="table ucp-table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Full Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedCustomers.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor:
                          customerId === row.id ? "#e3f2fd" : "white",
                      }}
                    >
                      <td>{row.id}</td>
                      <td>{row.fullName}</td>
                      <td>{row.phone}</td>
                      <td>{row.email}</td>

                      <td>
                        {row.gender === "active" ? (
                          <span style={{ color: "green", fontWeight: "bold" }}>
                            Active
                          </span>
                        ) : (
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            Inactive
                          </span>
                        )}
                      </td>

                      <td>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => selectCustomer(row)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 15,
                  gap: 10,
                }}
              >
                <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Prev
                </Button>

                <span style={{ paddingTop: 6 }}>
                  Page {page} of {totalPages || 1}
                </span>

                <Button
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {filteredCustomers.length === 0 && (
            <div style={{ marginTop: 20 }}>Search to display customers.</div>
          )}
        </div>
      </div>

      <hr />

      {/* Product */}
      {/* ================= PRODUCT SECTION ================= */}
      <div
        ref={productSectionRef}
        style={{
          opacity: customerId ? 1 : 0.5,
          pointerEvents: customerId ? "auto" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <h4>Add Products</h4>

        {/* Product Search */}
        <div style={{ maxWidth: 400, marginBottom: 20 }}>
          <TextField
            variant="outlined"
            placeholder="Search Product"
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            style={{ marginTop: 10 }}
            onClick={searchProducts}
          >
            Search Product
          </Button>
        </div>

        <div style={{ display: "flex", gap: 30 }}>
          {/* LEFT: Product List */}
          <div style={{ flex: 1 }}>
            {filteredProducts.map((p) => (
              <div
                key={p.id}
                onClick={() => selectProduct(p)}
                style={{
                  display: "flex",
                  gap: 15,
                  padding: 10,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  marginBottom: 10,
                  cursor: "pointer",
                  background:
                    selectedProduct && selectedProduct.id === p.id
                      ? "#e3f2fd"
                      : "#fff",
                }}
              >
                <img
                  src={p.photo}
                  alt={p.name}
                  width="70"
                  height="70"
                  style={{ objectFit: "cover", borderRadius: 6 }}
                />
                <div>
                  <strong>{p.name}</strong>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: Variants */}
          <div style={{ flex: 1 }}>
            {selectedProduct && (
              <div>
                <h5>{selectedProduct.name} Variants</h5>

                {variants.map((v) => {
                  const checked =
                    variantIds.length > 0 && variantIds[0].id === v.id;

                  return (
                    <div
                      key={v.id}
                      onClick={() => {
                        if (checked) {
                          // unselect if clicked again
                          setVariantIds([]);
                        } else {
                          // only ONE variant allowed
                          setVariantIds([
                            {
                              id: v.id,
                              price: v.variantPrice,
                              name: v.variantName,
                            },
                          ]);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: 10,
                        marginBottom: 8,
                        cursor: "pointer",
                        border: checked
                          ? "2px solid #1976d2"
                          : "1px solid #ddd",
                        borderRadius: 6,
                        background: checked ? "#e3f2fd" : "#fff",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {/* Custom Checkbox */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          border: "2px solid #1976d2",
                          borderRadius: 4,
                          background: checked ? "#1976d2" : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 14,
                          fontWeight: "bold",
                        }}
                      >
                        {checked ? "✓" : ""}
                      </div>

                      <div>
                        {v.variantName} - ₹{v.variantPrice}
                      </div>
                    </div>
                  );
                })}

                <Button
                  variant="contained"
                  color="secondary"
                  style={{ marginTop: 10 }}
                  onClick={addProductToOrder}
                >
                  Add Product
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <hr />

      <hr />

      <h4>Order Summary</h4>

      {orderProducts.map((item, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #ddd",
            padding: 15,
            marginBottom: 10,
            borderRadius: 8,
            background: "#fff",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>{item.productName}</strong>

            <Button
              size="small"
              color="secondary"
              onClick={() => removeProductFromOrder(index)}
            >
              Remove
            </Button>
          </div>

          {item.variants.map((v, i) => (
            <div key={i}>
              {v.name} - ₹{v.price}
            </div>
          ))}
        </div>
      ))}

      <hr />

      <div style={{ maxWidth: 400 }}>
        <TextField
          label="Order ID"
          variant="outlined"
          fullWidth
          margin="normal"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
        />

        <TextField
          label="Order Date"
          type="date"
          variant="outlined"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={orderDate}
          onChange={(e) => setOrderDate(e.target.value)}
        />

        <TextField
          label="Grand Total"
          variant="outlined"
          fullWidth
          margin="normal"
          value={grandTotal}
          onChange={(e) => setGrandTotal(e.target.value)}
          placeholder="Enter grand total (e.g. 60.00)"
        />
      </div>

      <Button
        variant="contained"
        style={{
          background: "linear-gradient(45deg, #1976d2, #42a5f5)",
          color: "#fff",
          padding: "12px 40px",
          fontSize: 16,
          marginTop: 20,
          borderRadius: 30,
        }}
        onClick={submitOrder}
      >
        Place Order
      </Button>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/components/admin/view/order/addorder/index.js